import { useRef, useState, type ReactNode } from "react";
import { Text } from "../../../../atoms/Text";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../../../molecules/HoverCard";
import { cn } from "../../../../lib/cn";
import { showBelowClass } from "../collapse";
import { LEAD_WIDTH, type LedgerCollapse } from "../types";

interface LedgerRowTextProps {
  /** Generic lead slot: a status dot, a hollow dot, a deduction minus, anything. */
  lead?: ReactNode;
  title: ReactNode;
  /** Trailing slot on the title line: part tag, kind tag, percentage. */
  titleTag?: ReactNode;
  /** Content shown only at or below the collapse point. */
  meta?: ReactNode;
  collapse: LedgerCollapse;
  /** Rendered in a hover card anchored to the title line. */
  hoverCard?: ReactNode;
}

/**
 * Internal text block for ledger rows. Not exported from the ledger index.
 *
 * The hover card anchors to the title line rather than the row: a full-width
 * anchor pushes `right-start` off the viewport and the card flips to the
 * left, away from the thing being described.
 */
export const LedgerRowText = ({
  lead,
  title,
  titleTag,
  meta,
  collapse,
  hoverCard,
}: LedgerRowTextProps) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const suppressPreview = useRef(false);
  const titleLine = (
    <div
      className="inline-flex min-w-0 max-w-full items-center gap-2"
      onPointerDown={() => {
        suppressPreview.current = true;
        setPreviewOpen(false);
      }}
      onPointerLeave={() => {
        suppressPreview.current = false;
      }}
    >
      {lead != null ? (
        <div className="flex w-2 shrink-0 justify-center">{lead}</div>
      ) : null}
      <Text
        as="span"
        variant="inherit"
        className="truncate font-medium text-(length:--ledger-text-title) leading-[1.43]"
      >
        {title}
      </Text>
      {titleTag}
    </div>
  );

  return (
    <div className="min-w-0 flex-1">
      {hoverCard ? (
        <HoverCard
          open={previewOpen}
          onOpenChange={(open) => {
            if (!open || !suppressPreview.current) {
              setPreviewOpen(open);
            }
          }}
          closeDelay={100}
        >
          <HoverCardTrigger asChild>{titleLine}</HoverCardTrigger>
          <HoverCardContent side="right" align="start">
            {hoverCard}
          </HoverCardContent>
        </HoverCard>
      ) : (
        titleLine
      )}
      {meta != null && meta !== "" && (
        <Text
          as="div"
          variant="inherit"
          color="secondary"
          className={cn(
            "truncate text-(length:--ledger-text-cell) leading-[1.66]",
            showBelowClass(collapse),
          )}
          style={lead != null ? { paddingLeft: LEAD_WIDTH + 10 } : undefined}
        >
          {meta}
        </Text>
      )}
    </div>
  );
};
